import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Item as ToggleGroupItem,
  type ToggleGroupItemProps,
  type ToggleGroupMultipleProps,
  Root as ToggleGroupRoot,
  type ToggleGroupSingleProps,
} from "@radix-ui/react-toggle-group";

import { cn } from "@btnet/utilities-core";

/**
 * Base props shared by both single and multiple selection modes.
 *
 * `data-testid` is omitted because `testid` maps to it — accepting both would cause conflicts.
 */
interface IBuiSegmentedControlBaseProps {
  testid: string;
  /** Accessible name for the group — describes what the options control (e.g. "View mode"). */
  "aria-label": string;
  children?: ReactNode | undefined;
  className?: string | undefined;
  /** When true, the control stretches to fill its container and segments share equal width. */
  fullWidth?: boolean | undefined;
}

/**
 * Single-selection segmented control.
 * Exactly one option can be active at a time.
 */
export interface IBuiSegmentedControlSingleProps
  extends Omit<ToggleGroupSingleProps, "data-testid" | "aria-label">,
    IBuiSegmentedControlBaseProps {}

/**
 * Multiple-selection segmented control.
 * Any combination of options can be active simultaneously.
 */
export interface IBuiSegmentedControlMultipleProps
  extends Omit<ToggleGroupMultipleProps, "data-testid" | "aria-label">,
    IBuiSegmentedControlBaseProps {}

export type BuiSegmentedControlProps =
  | IBuiSegmentedControlSingleProps
  | IBuiSegmentedControlMultipleProps;

/**
 * BuiSegmentedControl.Option props — extends Radix ToggleGroupItemProps with testid.
 * Provides full access to the library's Option API with Blueprint styling.
 *
 * Note: `children` is explicitly included due to React 18 type changes.
 */
export interface IBuiSegmentedControlOptionProps
  extends Omit<ToggleGroupItemProps, "data-testid"> {
  testid: string;
  children?: ReactNode | undefined;
  /** When true, applies tighter horizontal padding suited for icon-only content. */
  iconOnly?: boolean | undefined;
}

/**
 * Detects horizontal overflow on a scrollable container.
 * Recalculates on scroll events and element resizes.
 *
 * @returns `canScrollLeft` / `canScrollRight` — whether content overflows in each direction.
 * @returns `scrollRef` — attach to the scrollable element.
 */
const useScrollOverflow = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(checkScroll);
      observer.observe(el);
    }
    return () => {
      el.removeEventListener("scroll", checkScroll);
      observer?.disconnect();
    };
  }, [checkScroll]);

  return {
    canScrollLeft,
    canScrollRight,
    scrollRef,
  };
};

// The forwarded ref targets the outer wrapper div (for layout measurement),
// not the inner Radix ToggleGroupRoot.
const BuiSegmentedControlRoot = forwardRef<
  HTMLDivElement,
  BuiSegmentedControlProps
>(({ testid, className, fullWidth = false, children, ...props }, ref) => {
  const { canScrollLeft, canScrollRight, scrollRef } = useScrollOverflow();

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={cn(
        "relative max-w-full rounded-lg border border-default bg-gray-5",
        fullWidth ? "flex w-full" : "inline-flex w-fit",
        className,
      )}
    >
      <ToggleGroupRoot
        ref={scrollRef}
        className={cn(
          "flex items-stretch overflow-x-auto p-1",
          fullWidth && "w-full",
        )}
        {...props}
      >
        {children}
      </ToggleGroupRoot>
      <span
        aria-hidden
        className={cn(
          // eslint-disable-next-line btnet/no-tailwind-arbitrary-values -- No BDS directional overflow token exists yet; uses the BDS overflow color via CSS var
          "absolute inset-y-0 left-0 w-6 rounded-l-lg shadow-[inset_10px_0_8px_-8px_var(--bui-token-elevation-color-overflow)] pointer-events-none transition-opacity z-1",
          canScrollLeft ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden
        className={cn(
          // eslint-disable-next-line btnet/no-tailwind-arbitrary-values -- No BDS directional overflow token exists yet; uses the BDS overflow color via CSS var
          "absolute inset-y-0 right-0 w-6 rounded-r-lg shadow-[inset_-10px_0_8px_-8px_var(--bui-token-elevation-color-overflow)] pointer-events-none transition-opacity z-1",
          canScrollRight ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden
        className="absolute inset-0 rounded-lg shadow-sunken pointer-events-none"
      />
    </div>
  );
});
BuiSegmentedControlRoot.displayName = "BuiSegmentedControlRoot";

const defaultOptionClasses = cn(
  // Reset button defaults & layout (horizontal padding applied separately via iconOnly prop)
  "appearance-none bg-transparent box-border inline-flex items-center justify-center gap-1 relative",
  // Responsive height: 36px mobile, 28px desktop
  "h-9 md:h-7",
  // Shape & border
  "rounded-md border border-transparent",
  // Typography
  "font-sans text-sm font-medium text-gray-60",
  // Interaction
  "cursor-pointer whitespace-nowrap select-none",
  // Transitions
  "transition-all duration-150 ease-in-out",
  // Selected state
  "data-[state=on]:bg-gray-0 data-[state=on]:border-gray-15 data-[state=on]:shadow-raised data-[state=on]:text-gray-90",
  // Hover (unselected)
  "data-[state=off]:hover:bg-gray-10",
  // Focus
  "bui-focus-ring",
  // Disabled
  "data-disabled:text-gray-40 data-disabled:cursor-not-allowed",
  // Disabled + selected
  "data-disabled:data-[state=on]:bg-gray-5 data-disabled:data-[state=on]:border-gray-15 data-disabled:data-[state=on]:shadow-none data-disabled:data-[state=on]:text-gray-40",
);

const BuiSegmentedControlOption = forwardRef<
  HTMLButtonElement,
  IBuiSegmentedControlOptionProps
>(({ testid, className, asChild = false, iconOnly = false, ...props }, ref) => (
  <ToggleGroupItem
    ref={ref}
    data-testid={testid}
    asChild={asChild}
    className={cn(
      !asChild && defaultOptionClasses,
      !asChild && (iconOnly ? "px-2" : "px-3"),
      className,
    )}
    {...props}
  />
));
BuiSegmentedControlOption.displayName = "BuiSegmentedControlOption";

/**
 * BuiSegmentedControl — a styled wrapper around @radix-ui/react-toggle-group.
 * Provides full access to the library's API with Blueprint styling.
 *
 * Use `type="single"` for segmented control behavior (one active item)
 * or `type="multiple"` to allow any combination of active items.
 *
 * Item height is responsive: 36px on mobile, 28px on desktop (md breakpoint).
 *
 * @param props.testid - Test ID for the segmented control container.
 * @param props.type - Selection mode: `"single"` or `"multiple"`.
 */
export const BuiSegmentedControl = Object.assign(BuiSegmentedControlRoot, {
  /**
   * BuiSegmentedControl.Option — wrapper around Radix ToggleGroupItem.
   *
   * **Default mode:** renders a styled button with selected/unselected visual states.
   * Pass text, icons, or any ReactNode as children.
   *
   * **Composition mode (`asChild`):** pass a single child element to take full
   * control of rendering. Radix merges toggle behavior (data-state, aria attrs,
   * keyboard nav) onto your element; default button styling is skipped.
   *
   * @param props.testid - Test ID for the option.
   * @param props.value - Unique string value identifying this option within the group.
   * @param props.asChild - When true, renders the child element instead of a button
   *   and skips default styling. The child receives Radix's toggle behavior via props.
   */
  Option: BuiSegmentedControlOption,
});
